'use client';

import { cn } from '@/lib/utils';
import { FileText } from 'lucide-react';
import { Play } from 'lucide-react';
import { Music } from 'lucide-react';
import { ShoppingCart } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import React from 'react';
import Image from 'next/image';
import { MessageSquareText } from 'lucide-react';
import { GraduationCap } from 'lucide-react';
import { ChartNoAxesColumnIncreasing } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { Pointer } from 'lucide-react';
import InspirationalSmsDialogue from '@/components/InspirationalSmsDialogue';
import { Separator } from '../ui/separator';

const CommonNav = ({ showIcons = false, mainContainerClassNames = '' }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const category = searchParams.get('category');
  const { data: session } = useSession();

  const role = session && session?.user && session.user.role === 'consumer';

  const pages = [
    {
      title: 'Videos',
      url: role ? '/chat' : '/videos',
      check: pathname.startsWith(role ? '/chat' : '/videos'),
      icon: <Play className="size-6 fill-white text-white sm:size-9" />,
    },
    {
      title: 'Self-Test',
      url: '/assessment',
      check: pathname.startsWith('/assessment'),
      image: '/assets/icons/nav/self-test.png',
    },
    {
      title: 'Blogs',
      url: '/blogs',
      check: pathname === '/blogs' && !category,
      icon: <FileText className="size-6 text-white sm:size-9" />,
    },
    {
      title: 'Music',
      url: '/blogs/smartguy-music',
      icon: <Music className="size-6 text-white sm:size-9" />,
    },
    {
      title: 'Merch',
      url: 'https://smartguystore.net/',
      icon: <ShoppingCart className="size-6 text-white sm:size-9" />,
    },
  ];

  const navigationLinks = [
    {
      title: 'Videos',
      url: role ? '/chat' : '/videos',
      check: pathname.startsWith(role ? '/chat' : '/videos'),
      icon: <Play className="size-6 fill-white text-white sm:size-9" />,
    },
    {
      title: 'Blogs',
      url: '/blogs',
      check: pathname === '/blogs' && !category,
      icon: <FileText className="size-6 text-white sm:size-9" />,
    },
    {
      title: 'Music',
      url: '/blogs/smartguy-music',
      icon: <Music className="size-6 text-white sm:size-9" />,
    },
    {
      title: 'Merch',
      url: 'https://smartguystore.net/',
      icon: <ShoppingCart className="size-6 text-white sm:size-9" />,
    },
    {
      title: 'Daily Message',
      url: 'https://www.youtube.com/playlist?list=PLfhxxUq_8RG6SqC25cnN1cSmESxs0nzgX',
      // image: '/assets/icons/nav/daily-message.png',
      icon: <MessageSquareText className="size-6 text-white sm:size-9" />,
    },
    {
      title: 'RCIA/OCIA',
      url: '/blogs/30-day-faith-journey',
      check: pathname.startsWith('/blogs'),
      // image: '/assets/icons/nav/ocia-classes.png',
      icon: <GraduationCap className="size-6 text-white sm:size-9" />,
    },
    {
      title: 'Member Polls',
      url: 'https://www.youtube.com/@SMARTGUYAcademy/community',
      // image: '/assets/icons/nav/member-polls.png',
      icon: (
        <ChartNoAxesColumnIncreasing
          strokeWidth={3.5}
          className="size-6 fill-white text-white sm:size-9"
        />
      ),
    },
    {
      title: 'Self-Test',
      url: '/assessment',
      check: pathname.startsWith('/assessment'),
      image: '/assets/icons/nav/self-test.png',
    },
  ];

  return (
    <>
      <div className="mx-auto max-w-lg">
        {!showIcons && (
          <div
            className={cn(
              'flex flex-wrap items-center justify-center gap-x-5 gap-y-0 px-3 py-3',
              mainContainerClassNames
            )}
          >
            {!showIcons &&
              pages.map((page, index) => {
                const isExternalHttpLink = /^https?:\/\//i.test(page.url);

                return (
                  <Link
                    key={index}
                    href={`${page.url}`}
                    target={isExternalHttpLink ? '_blank' : undefined}
                    rel={isExternalHttpLink ? 'noopener noreferrer' : undefined}
                    className={cn(
                      'h-auto text-[.85rem] font-semibold hover:underline',
                      page.check ? 'font-semibold text-black' : 'text-primary'
                    )}
                  >
                    {page.title}
                  </Link>
                );
              })}
          </div>
        )}

        {showIcons && (
          <>
            <div className="mt-4 mb-4 px-3 sm:mt-6">
              <Link
                href={'/pages/explore-more'}
                className={cn(
                  'from-primary/80 to-primary bg-gradient-to-b transition-all duration-300',
                  'shadow-lg',
                  'flex w-full items-center justify-between gap-3 rounded-2xl px-4 py-3',
                  'sm:gap-4 sm:px-[32px] sm:py-3.5'
                )}
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white sm:size-16">
                  <Play className="fill-primary text-primary size-5 sm:size-10" />
                </div>
                <div className="font-figtree flex flex-1 flex-col items-center text-center leading-tight">
                  <span className="text-xl font-bold tracking-wide text-white uppercase sm:text-3xl">
                    Free Videos
                  </span>
                  <span
                    className={cn(
                      'font-figtree text-xl font-bold tracking-wide text-cyan-500 uppercase sm:text-3xl'
                    )}
                  >
                    & Classes!
                  </span>
                </div>
                <div className="h-18 overflow-hidden border-[1px] border-white bg-white" />
                <ArrowRight className="size-8 shrink-0 text-white sm:size-10" />
              </Link>
              <div
                className={cn(
                  'flex w-full items-center justify-center gap-2',
                  'border-green-250',
                  'bg-gradient-to-b from-green-100 to-green-100 transition-all duration-300',
                  'mt-3 rounded-2xl px-3 py-2 shadow-lg hover:underline sm:gap-3'
                )}
              >
                <Link
                  href="/pages/share-a-video-save-a-life"
                  className="flex w-full items-center justify-between gap-2 px-7 hover:underline sm:gap-3"
                >
                  <span aria-hidden="true" className="">
                    <Image
                      src="/assets/icons/heart-in-hand-green.svg"
                      alt="Share a Video - Save a life"
                      width={50}
                      height={50}
                      className="object-contain"
                    />
                    {/* <Pointer className="size-6 rotate-120 text-green-500 sm:size-9" /> */}
                  </span>
                  <span
                    className={cn(
                      'font-figtree text-primary text-sm font-bold tracking-wide',
                      'sm:text-base',
                      'lg:text-xl'
                    )}
                  >
                    Share a Video - Save a life
                  </span>
                  <ArrowRight className="text-primary size-8 shrink-0 font-bold" />
                </Link>
              </div>

              <InspirationalSmsDialogue
                variant="hero"
                triggerLabel="Subscribe to Daily Scripture"
              />

              {/* <div className="mt-4 flex items-center gap-3">
                <span className="h-px flex-1 bg-gray-300" />
                <p className="text-primary shrink-0 text-center text-xs font-semibold italic sm:text-sm">
                  Faith. Purpose. Growth. All In One Place.
                </p>
                <span className="h-px flex-1 bg-gray-300" />
              </div> */}
            </div>

            <div
              className={cn(
                'grid grid-cols-4 place-items-center gap-x-5 gap-y-0 px-3',
                mainContainerClassNames
              )}
            >
              {navigationLinks.map((page, index) => {
                const isExternalHttpLink = /^https?:\/\//i.test(page.url);

                return (
                  <Link
                    key={index}
                    href={`${page.url}`}
                    target={isExternalHttpLink ? '_blank' : undefined}
                    rel={isExternalHttpLink ? 'noopener noreferrer' : undefined}
                    className="text-primary flex flex-col items-center justify-center gap-2"
                  >
                    {page?.icon && (
                      <div
                        className={cn(
                          'from-primary/70 to-primary bg-gradient-to-b transition-all duration-300',
                          'flex h-12 w-12 items-center justify-center rounded-full',
                          'sm:h-20 sm:w-20',
                          page?.iconContainerClassNames
                        )}
                      >
                        {page.icon}
                      </div>
                    )}

                    {page.image && (
                      <div className="relative h-12.5 w-12.5 object-contain sm:h-20.5 sm:w-20.5">
                        <Image
                          src={page.image}
                          alt={page.title}
                          fill
                          className={cn('object-contain')}
                        />
                      </div>
                    )}

                    {page.title && (
                      <span
                        className={cn(
                          'font-figtree min-h-10 text-center text-[0.75rem] font-bold sm:min-h-15 sm:text-[1.2rem]',
                          page.check ? 'font-bold text-black' : 'text-primary'
                        )}
                      >
                        {page.title}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CommonNav;
